import type { DeliveryMethodType, CustomAddress } from '@libs/common-types';
import { logger } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

/*
    A pure API client that knows nothing about business logic —
    it just follows the URL and throws an error if there is no result.

  For SSR
    - Returns basic product information ONLY
    - Should be called from the server side to get general information
    - Param - Web Style ID or SKU
 */
type GetProductBase = {
    id: string;
};

export async function getProductBase<T>({ id }: GetProductBase): Promise<T> {
    const url = CWEA_ENDPOINTS.product.getProductBase(id);
    logger.info(`[GET PRODUCT BASE]: ${url}`);

    const result: Nullable<T> = await cweaClient.get(url, { useOldFetch: true });

    // Guard app layer from null response
    if (!result) {
        logger.error(`[GET PRODUCT BASE][Error]: ${url}`);

        throw new Error(`[GET PRODUCT BASE]: ${url} returned null`);
    }

    return result;
}

/*
  For CSR - Client Side Rendering
    - Returns tailored delivery information for a specific SKU
    - Should be called from the client side to get inventory and store-specific data
    - Param - SKU
 */
type GetProductTailored = {
    deliveryMethodsToRetrieve: DeliveryMethodType[];
    storeId?: string;
    customAddress?: CustomAddress;
    variantSkus?: string[];
};

export async function getProductTailored<T>({
    storeId,
    customAddress,
    deliveryMethodsToRetrieve,
    variantSkus = [],
}: GetProductTailored): Promise<T> {
    const url = CWEA_ENDPOINTS.product.getProductTailored;
    logger.info(`[GET PRODUCT TAILORED]: ${url}`);

    const body = {
        storeId,
        customAddress,
        deliveryMethodsToRetrieve,
        variantSkus,
    };

    const result: Nullable<T> = await cweaClient.post(url, { body, useOldFetch: true });

    // Guard app layer from null response
    if (!result) {
        logger.error(`[GET PRODUCT TAILORED][Error]: ${url}`);

        throw new Error(`[GET PRODUCT TAILORED]: ${url} returned null`);
    }

    return result;
}
